import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { LocalStorageKeyType } from '~/enums/LocalStorageKeyType';
import { useLocalStorageState } from '~/hooks/useLocalStorageState';

import { recordSidebarNavPreferenceMetric } from './recordSidebarNavPreferenceMetric';

interface SidebarNavPreferenceContextValue {
  isOptedIn: boolean;
  isOnboardingVisible: boolean;
  optIn: () => void;
  optOut: () => void;
  dismissOnboarding: () => void;
}

const SidebarNavPreferenceContext =
  createContext<SidebarNavPreferenceContextValue | null>(null);

export const SidebarNavPreferenceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOptedIn, setIsOptedIn] = useLocalStorageState(
    LocalStorageKeyType.SidebarNavOptIn,
    false,
  );
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(false);

  const optIn = useCallback(() => {
    setIsOptedIn(true);
    setIsOnboardingVisible(true);
    recordSidebarNavPreferenceMetric('opt_in');
  }, [setIsOptedIn]);

  const optOut = useCallback(() => {
    setIsOptedIn(false);
    recordSidebarNavPreferenceMetric('opt_out');
  }, [setIsOptedIn]);

  const dismissOnboarding = useCallback(() => {
    setIsOnboardingVisible(false);
  }, []);

  const value = useMemo(
    () => ({
      isOptedIn,
      isOnboardingVisible,
      optIn,
      optOut,
      dismissOnboarding,
    }),
    [isOptedIn, isOnboardingVisible, optIn, optOut, dismissOnboarding],
  );

  return (
    <SidebarNavPreferenceContext.Provider value={value}>
      {children}
    </SidebarNavPreferenceContext.Provider>
  );
};

export const useSidebarNavPreference = () => {
  const context = useContext(SidebarNavPreferenceContext);
  if (!context) {
    throw new Error(
      'useSidebarNavPreference must be used within SidebarNavPreferenceProvider',
    );
  }
  return context;
};
